import { RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { useAtomValue } from "jotai";

import type { ShipmentManagementPickupDeliveryDetails } from "~/shared/schemas/shipment-management-schema.ts";
import type { StopName } from "~/shared/types/stop-types.ts";
import {
	formatDateTime,
	formatDateToNumeric,
} from "~/shared/utils/date-formatter.ts";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";

import { isEditingShipmentAtom } from "../../state/shipment-edit-atoms.ts";
import { PICKUP_DELIVERY_DETAILS_DEFAULT } from "../../utils/defaults.ts";
import { ShipmentDetailEdit } from "./shipment-detail-edit.tsx";

export const ShipmentDetail = ({
	details = PICKUP_DELIVERY_DETAILS_DEFAULT,
	name,
	customerId = 0,
}: {
	details?: ShipmentManagementPickupDeliveryDetails;
	name: StopName;
	customerId?: number;
}) => {
	const isEditing = useAtomValue(isEditingShipmentAtom);

	if (isEditing) {
		return (
			<ShipmentDetailEdit
				customerId={customerId}
				details={details}
				name={name}
			/>
		);
	}

	const isFCFS = details.appointmentType === "FCFS";
	const label = name === "pickup" ? "Pickup" : "Delivery";
	const fullLocation = [details.companyName, details.address]
		.filter(Boolean)
		.join(", ");
	return (
		<div
			className={"shipment-stop shipment-detail"}
			data-testid={`${name}-shipment-detail`}
		>
			<div className={"details"}>
				<div className={"company-details"}>
					<p className={"section-label"}>{label} Location</p>
					<span
						className={"shipment-detail-location"}
						data-testid={"shipment-detail-company-name"}
						title={fullLocation}
					>
						{details.companyName}
						{details.companyName && details.address ? ", " : null}
						<span data-testid={"shipment-detail-address"}>
							{details.address}
						</span>
					</span>
				</div>
				<div className={"location-type"}>
					<p className={"section-label"}>Location Type</p>
					<div className={"location"}>
						{" "}
						<RadioButton
							checked={true}
							className={"location-radio"}
							readOnly={true}
						/>
						<span data-testid={"shipment-detail-location-type"}>
							{details.locationType}
						</span>
					</div>
				</div>
				<div className={"accessorials"}>
					<p className={"section-label"}>Accessorials</p>
					<div
						className={"accessorial"}
						data-testid={"shipment-detail-accessorials"}
					>
						{" "}
						{details.accessorials?.length
							? details.accessorials.join(", ")
							: "None"}
					</div>
				</div>
				<div className={"contact-details"}>
					<div className={"contact-row contact-row-labels"}>
						<p className={"section-label"}>Contact Name</p>
						<p className={"section-label"}>Phone</p>
						<p className={"section-label"}>EXT</p>
					</div>
					<div className={"contact-row contact-row-values"}>
						<p data-testid={"shipment-detail-contact-name"}>
							{details.contact?.name || "N/A"}
						</p>
						<p data-testid={"shipment-detail-contact-phone"}>
							{details.contact?.phone
								? formatPhoneNumber(details.contact.phone)
								: "N/A"}
						</p>
						<p data-testid={"shipment-detail-contact-extension"}>
							{details.contact?.extension || "N/A"}
						</p>
					</div>
				</div>
				<div className={"hours-details"}>
					{isFCFS ? (
						<>
							<div className={"hours-label-row"}>
								<p className={"section-label"}>{label} Hours</p>
								<span className={"hours-appointment-type"}>FCFS</span>
							</div>
							<div className={"contact-row contact-row-values"}>
								<p data-testid={"shipment-detail-hours"}>
									{details.shipmentHoursFrom} - {details.shipmentHoursTo}
								</p>
							</div>
						</>
					) : (
						<div className={"scheduled-actual-details"}>
							<div>
								<p className={"section-label"}>
									{name === "pickup" ? "Pickup" : "Delivery"} Hours From
								</p>
								<p data-testid={"shipment-detail-hours"}>
									{details.shipmentHoursFrom}
								</p>
							</div>
							<div>
								<p className={"section-label"}>{label} Hours To</p>
								<p>{details.shipmentHoursTo}</p>
							</div>
						</div>
					)}
				</div>
				<div className={"scheduled-actual-details"}>
					<div>
						<p className={"section-label"}>Scheduled</p>
						<p data-testid={"shipment-detail-scheduled"}>
							{formatDateToNumeric(details.scheduled)}
						</p>
					</div>
					<div>
						<p className={"section-label"}>Actual</p>
						<p data-testid={"shipment-detail-actual"}>
							{formatDateTime(details.actual)}
						</p>
					</div>
				</div>
				<div className={"scheduled-actual-details"}>
					<div>
						<p className={"section-label"}>
							{name === "pickup" ? "Pickup" : "Delivery"} #
						</p>
						<p data-testid={"shipment-detail-number"}>
							{details.shipmentNumber ?? "N/A"}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
